<script setup>
import { computed } from "vue";
import { useStore } from "vuex";

const store = useStore();
const canAdd = computed(() => store.getters.hasPermission("expense.add"));
const canEdit = computed(() => store.getters.hasPermission("expense.edit"));
const canDelete = computed(() => store.getters.hasPermission("expense.delete"));
</script>

<template>
  <div class="main-wrapper">
    <layouts></layouts>
    <sidebar></sidebar>
    <div class="page-wrapper">
      <div class="content">
        <pageaddheader :title="title" :title1="title1" :items="canAdd ? items : []" />

        <div class="card expense-list-card">
          <div class="card-body">
            <div class="expense-toolbar">
              <div class="toolbar-row">
                <div class="filter-grid">
                  <div class="form-group">
                    <label>From Date</label>
                    <input v-model="filters.date_from" type="date" class="form-control" />
                  </div>
                  <div class="form-group">
                    <label>To Date</label>
                    <input v-model="filters.date_to" type="date" class="form-control" />
                  </div>
                  <div class="form-group">
                    <label>Category</label>
                    <select v-model="filters.categoryid" class="form-control">
                      <option value="">All Categories</option>
                      <option v-for="category in categories" :key="category.categoryid" :value="category.categoryid">
                        {{ category.categoryname }}
                      </option>
                    </select>
                  </div>
                  <div class="form-group">
                    <label>Payment Type</label>
                    <select v-model="filters.paymentmode" class="form-control">
                      <option value="">All Types</option>
                      <option v-for="mode in paymentModes" :key="mode.value" :value="mode.value">
                        {{ mode.label }}
                      </option>
                    </select>
                  </div>
                  <div class="form-group search-group">
                    <label>Search</label>
                    <input
                      v-model="filters.search"
                      type="search"
                      class="form-control"
                      placeholder="Bill no, payee, remarks..."
                      @keyup.enter="loadExpenses"
                    />
                  </div>
                  <div class="form-group action-group">
                    <label class="action-label">Actions</label>
                    <div class="button-group">
                      <button type="button" class="btn btn-submit filter-btn" @click="loadExpenses">View</button>
                      <button type="button" class="btn btn-outline-secondary filter-btn" @click="clearFilters">Reset</button>
                    </div>
                  </div>
                </div>

                <div class="toolbar-export">
                  <button
                    type="button"
                    class="export-btn"
                    title="Download Excel"
                    :disabled="exportLoading"
                    @click="exportToExcel"
                  >
                    <img src="../../../assets/img/icons/excel.svg" alt="Export Excel" class="excel-icon" />
                  </button>
                </div>
              </div>
            </div>

            <div class="summary-strip">
              <div>
                <span>Total Expenses</span>
                <strong>{{ expenses.length }}</strong>
              </div>
              <div>
                <span>Total Amount</span>
                <strong>Rs. {{ formatAmount(totalAmount) }}</strong>
              </div>
            </div>

            <div class="table-responsive">
              <a-table :columns="columns" :data-source="expenses" row-key="expenseid" :loading="loading">
                <template #bodyCell="{ column, record }">
                  <template v-if="column.key === 'amount'">
                    {{ formatAmount(record.amount) }}
                  </template>
                  <template v-else-if="column.key === 'paymentmode'">
                    {{ formatPaymentMode(record.paymentmode) }}
                  </template>
                  <template v-else-if="column.key === 'action' && (canEdit || canDelete)">
                    <a v-if="canEdit" class="me-3" href="javascript:void(0);" @click="editExpense(record)">
                      <img src="../../../assets/img/icons/edit.svg" alt="Edit" />
                    </a>
                    <a v-if="canDelete" class="me-3 confirm-text" href="javascript:void(0);" @click="deleteExpense(record.expenseid)">
                      <img src="../../../assets/img/icons/delete.svg" alt="Delete" />
                    </a>
                  </template>
                </template>
              </a-table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

const paymentModes = [
  { value: "CASH", label: "Cash" },
  { value: "BANK_TRANSFER", label: "Bank Transfer" },
  { value: "UPI", label: "UPI" },
  { value: "CHEQUE", label: "Cheque" },
  { value: "OTHER", label: "Other" },
];

const columns = [
  { title: "Date", dataIndex: "expensedate", sorter: (a, b) => (a.expensedate || "").localeCompare(b.expensedate || "") },
  { title: "Category", dataIndex: "categoryname", sorter: (a, b) => (a.categoryname || "").localeCompare(b.categoryname || "") },
  { title: "Amount", dataIndex: "amount", key: "amount", sorter: (a, b) => Number(a.amount || 0) - Number(b.amount || 0) },
  { title: "Payment", dataIndex: "paymentmode", key: "paymentmode" },
  { title: "Payee", dataIndex: "payeename", sorter: (a, b) => (a.payeename || "").localeCompare(b.payeename || "") },
  { title: "Bill No", dataIndex: "billno", sorter: (a, b) => (a.billno || "").localeCompare(b.billno || "") },
  { title: "Remarks", dataIndex: "remarks", sorter: (a, b) => (a.remarks || "").localeCompare(b.remarks || "") },
  { title: "Action", key: "action" },
];

function monthStart() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`;
}

function today() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

export default {
  name: "expenselist",
  data() {
    return {
      title: "Expenses",
      title1: "View and manage expenses",
      items: [{ text: "Add New Expense", addname: "/createexpense" }],
      expenses: [],
      categories: [],
      loading: false,
      exportLoading: false,
      paymentModes,
      filters: {
        search: "",
        categoryid: "",
        paymentmode: "",
        date_from: monthStart(),
        date_to: today(),
      },
      columns,
    };
  },
  computed: {
    totalAmount() {
      return this.expenses.reduce((sum, expense) => sum + Number(expense.amount || 0), 0);
    },
  },
  methods: {
    buildFilterParams() {
      const params = {};
      Object.keys(this.filters).forEach((key) => {
        if (this.filters[key]) params[key] = this.filters[key];
      });
      return params;
    },
    formatAmount(value) {
      return Number(value || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatPaymentMode(value) {
      return (value || "").replace(/_/g, " ");
    },
    editExpense(expense) {
      this.$router.push({ path: "/editexpense", query: { id: expense.expenseid } });
    },
    clearFilters() {
      this.filters = {
        search: "",
        categoryid: "",
        paymentmode: "",
        date_from: monthStart(),
        date_to: today(),
      };
      this.loadExpenses();
    },
    async loadCategories() {
      const response = await axios.get("/v1/expenses/categories");
      this.categories = response.data || [];
    },
    async loadExpenses() {
      this.loading = true;
      try {
        const response = await axios.get("/v1/expenses", { params: this.buildFilterParams() });
        this.expenses = response.data || [];
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Failed to load expenses",
          text: error.response?.data?.detail || error.message,
        });
      } finally {
        this.loading = false;
      }
    },
    async exportToExcel() {
      this.exportLoading = true;
      try {
        const response = await axios.get("/v1/expenses/export", {
          params: this.buildFilterParams(),
          responseType: "blob",
        });
        const blob = new Blob(
          [response.data],
          { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" },
        );
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `expenses_${this.filters.date_from || today()}_${this.filters.date_to || today()}.xlsx`;
        link.click();
        URL.revokeObjectURL(link.href);
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Excel export failed",
          text: error.response?.data?.detail || error.message,
        });
      } finally {
        this.exportLoading = false;
      }
    },
    async deleteExpense(expenseid) {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "This expense will be deleted.",
        showCancelButton: true,
        confirmButtonColor: "#FF9F43",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      });
      if (!result.isConfirmed) return;
      try {
        await axios.delete(`/v1/expenses/${expenseid}`);
        Swal.fire("Deleted!", "Expense has been deleted.");
        await this.loadExpenses();
      } catch (error) {
        Swal.fire(error.response?.data?.detail || "Unable to delete expense");
      }
    },
  },
  async mounted() {
    await this.loadCategories();
    await this.loadExpenses();
  },
};
</script>

<style scoped>
.expense-list-card {
  border: none;
  box-shadow: 0 10px 30px rgba(31, 41, 55, 0.08);
}
.expense-toolbar {
  background: #f8fbff;
  border: 1px solid #eef2f7;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
}
.toolbar-row {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
}
.filter-grid {
  flex: 1;
  display: grid;
  grid-template-columns: 150px 150px minmax(140px, 1fr) 160px minmax(160px, 1fr) auto;
  gap: 12px;
  align-items: end;
}
.form-group {
  margin-bottom: 0;
}
.form-group label {
  display: block;
  font-size: 12px;
  font-weight: 700;
  color: #475569;
  margin-bottom: 5px;
}
.filter-grid .form-control {
  height: 38px;
  min-height: 38px;
}
.action-group {
  min-width: 170px;
}
.action-label {
  visibility: hidden;
}
.button-group {
  display: flex;
  gap: 8px;
  align-items: center;
}
.filter-btn {
  min-width: 78px;
  height: 38px;
  padding: 0 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  white-space: nowrap;
}
.toolbar-export {
  flex-shrink: 0;
  padding-bottom: 1px;
}
.export-btn {
  width: 42px;
  height: 42px;
  border: 1px solid #dbeafe;
  border-radius: 8px;
  background: #eff6ff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.18s ease;
}
.export-btn:hover:not(:disabled) {
  border-color: #3d5af1;
  background: #f0f4ff;
}
.export-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.excel-icon {
  width: 22px;
  height: 22px;
}
.summary-strip {
  display: grid;
  grid-template-columns: repeat(2, minmax(180px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}
.summary-strip div {
  padding: 12px 14px;
  border-radius: 8px;
  background: #111827;
  color: #fff;
}
.summary-strip span {
  display: block;
  font-size: 11px;
  opacity: 0.75;
}
.summary-strip strong {
  font-size: 18px;
}
@media (max-width: 992px) {
  .toolbar-row {
    flex-direction: column;
    align-items: stretch;
  }
  .filter-grid,
  .summary-strip {
    grid-template-columns: 1fr;
  }
  .toolbar-export {
    display: flex;
    justify-content: flex-end;
    padding-bottom: 0;
  }
}
</style>
